import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Plus, Minus } from "@phosphor-icons/react";

/**
 * FAQ section — answers the most common questions from PME prospects.
 *
 * Boosts SEO in two ways:
 *   1. Question-driven content with target keywords in H3s
 *   2. Schema.org FAQPage JSON-LD injected via Helmet → eligible for
 *      Google's rich-result FAQ snippet.
 */
const FAQS = [
    {
        q: "Combien coûte un site web pour une PME au Québec ?",
        a: "Un site vitrine PME chez Studio Cent Douze commence autour de 1&nbsp;200&nbsp;$ pour un site 1-3 pages, autour de 2&nbsp;200&nbsp;$ pour 4-6 pages, et environ 3&nbsp;800&nbsp;$ pour 7-12 pages. Un e-commerce démarre à 2&nbsp;800&nbsp;$ (avant taxes). L'outil de brief sur cette page calcule ton estimation en temps réel selon tes besoins.",
    },
    {
        q: "Quel est le délai de création d'un site web ?",
        a: "Pour un site vitrine standard, on livre en 4 à 8 semaines à partir de la validation du brief. Les projets e-commerce ou avec fonctionnalités sur mesure demandent 6 à 12 semaines. Un projet urgent est possible avec un supplément « fast-track ».",
    },
    {
        q: "Studio Cent Douze offre aussi photo, vidéo et création de contenu ?",
        a: "Oui. On est un studio multidisciplinaire basé à Magog : photo produit, portrait corporatif, capsules vidéo, création de contenu pour tes réseaux sociaux, motion design et post-production. Un seul contact pour toute ta production visuelle.",
    },
    {
        q: "Êtes-vous basés à Magog ? Travaillez-vous à l'extérieur de l'Estrie ?",
        a: "Oui, notre studio est basé à Magog, au cœur des Cantons-de-l'Est. On collabore régulièrement avec des PME partout au Québec (Montréal, Québec, Sherbrooke, Trois-Rivières) et en Europe francophone. Les projets 100&nbsp;% à distance sont notre quotidien.",
    },
    {
        q: "Que comprend l'identité de marque (branding) ?",
        a: "Notre livrable branding inclut : logo principal + déclinaisons, palette de couleurs, système typographique, guide de marque (brand guide PDF), déclinaisons cartes d'affaires, papeterie et gabarits réseaux sociaux. Tarif de base : 850&nbsp;$ pour un logo, 1&nbsp;400&nbsp;$ pour un guide de marque complet.",
    },
    {
        q: "Comment démarrer un projet avec vous ?",
        a: "Trois étapes : (1) remplis le brief interactif ci-dessous pour recevoir une estimation, (2) on te retourne une soumission détaillée sous 24-48&nbsp;h, (3) on planifie un appel découverte de 30 minutes via Calendly. Aucun engagement avant la signature du contrat.",
    },
    {
        q: "Offrez-vous de la maintenance et de la création de contenu en continu ?",
        a: "Oui, on propose des forfaits mensuels (mises à jour techniques, sauvegardes, sécurité, ajustements, publications réseaux sociaux, production de nouveaux visuels) à partir de 120&nbsp;$/mois. Tu peux aussi opter pour l'accompagnement à la demande facturé à l'heure.",
    },
];

export default function FAQ() {
    const [open, setOpen] = useState(0);

    // JSON-LD schema.org FAQPage for Google rich results
    const schema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: {
                "@type": "Answer",
                text: f.a.replace(/&nbsp;/g, " "),
            },
        })),
    };

    return (
        <section
            id="faq"
            data-testid="faq-section"
            className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 border-t border-scd"
        >
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(schema)}
                </script>
            </Helmet>

            <div className="grid md:grid-cols-12 gap-8 mb-14 md:mb-20">
                <div className="md:col-span-4">
                    <span className="label-eyebrow text-yellow">— FAQ</span>
                </div>
                <div className="md:col-span-8">
                    <h2
                        className="section-heading text-cream"
                        data-testid="faq-heading"
                    >
                        Les questions
                        <br />
                        <span className="text-yellow italic">
                            qu&apos;on nous pose souvent.
                        </span>
                    </h2>
                    <p className="mt-6 max-w-2xl text-cream/75 text-lg">
                        Prix, délais, méthodologie — voici les réponses aux
                        questions les plus fréquentes sur la conception de
                        sites web, la photo, la vidéo et le branding.
                    </p>
                </div>
            </div>

            <div className="max-w-3xl mx-auto grid gap-3">
                {FAQS.map((f, i) => (
                    <FAQItem
                        key={f.q}
                        q={f.q}
                        a={f.a}
                        open={open === i}
                        onToggle={() => setOpen(open === i ? -1 : i)}
                        index={i}
                    />
                ))}
            </div>
        </section>
    );
}

function FAQItem({ q, a, open, onToggle, index }) {
    return (
        <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: index * 0.04 }}
            data-testid={`faq-item-${index}`}
            className={`border rounded-sm bg-elev overflow-hidden transition-colors ${
                open ? "border-yellow" : "border-scd hover:border-green/50"
            }`}
        >
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                aria-controls={`faq-answer-${index}`}
                className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
            >
                <h3 className="font-display text-lg md:text-xl text-cream leading-snug">
                    {q}
                </h3>
                <span
                    className={`shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full border transition-colors ${
                        open
                            ? "bg-yellow border-yellow text-[#272727]"
                            : "border-scd text-cream/70"
                    }`}
                >
                    {open ? (
                        <Minus size={16} weight="bold" />
                    ) : (
                        <Plus size={16} weight="bold" />
                    )}
                </span>
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        id={`faq-answer-${index}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: "easeOut" }}
                        className="overflow-hidden"
                    >
                        <p
                            className="px-6 pb-6 text-cream/85 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: a }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
